import { SESSION_KEY_PAGE_POPUP_PUSHED_KEYS } from "@/constant/storageKey";
import { Persistent } from "@/utils/cache/persistent";

const pageKeysToGoBack = new Set();
let flushScheduled = false;

// 已 pushState 压入、且尚未回收的 pageKey（按压入顺序）
function getPushedPageKeys() {
  return Persistent.getSession(SESSION_KEY_PAGE_POPUP_PUSHED_KEYS) || [];
}

function setPushedPageKeys(pageKeys) {
  Persistent.setSession(SESSION_KEY_PAGE_POPUP_PUSHED_KEYS, pageKeys);
}

// PagePopupLayout 每次 pushState 成功后调用
export function markPagePopupPushed(pageKey) {
  if (!pageKey) return;

  // 现在点击物理返回键返回时，不会刷新pushedPageKeys，故去重
  setPushedPageKeys([...new Set([...getPushedPageKeys(), pageKey])]);
}

export function registerGoBackTask(pageKey) {
  if (!pageKey) return;

  pageKeysToGoBack.add(pageKey);

  // 同一 tick 内可能有多个 PagePopupLayout 关闭，只需要 flush 一次，
  // 否则 history.go 会因为 traversal 异步提交而被重复执行
  if (flushScheduled) return;
  flushScheduled = true;
  setTimeout(() => {
    flushScheduled = false;
    flushGoBackTasks();
  });
}

// 取出当前 hash query 中所有 page 参数对应的 pageKey（按 pushState 的先后顺序）
function getOpenedPageKeys() {
  const hashString = location.hash;
  const queryIndex = hashString.indexOf("?");
  if (queryIndex === -1) return [];

  return (
    hashString
      .slice(queryIndex + 1)
      .split("&")
      .map((qsPart) => qsPart.split("="))
      .filter((item) => item?.[0] === "page")
      // URL 里的值形如 `invoiceAdd_`，尾部下划线只是分隔符，比较前去掉
      .map((item) => (item?.[1] || "").replace(/_$/, ""))
      .filter(Boolean)
  );
}

// 去掉 hash query 中所有 page 参数，其余参数原样保留
function removePageParamsFromHref(href) {
  const hashIndex = href.indexOf("#");
  if (hashIndex === -1) return href;

  const hashString = href.slice(hashIndex + 1);
  const queryIndex = hashString.indexOf("?");
  if (queryIndex === -1) return href;

  const keptQueryString = hashString
    .slice(queryIndex + 1)
    .split("&")
    .filter((qsPart) => qsPart && qsPart.split("=")[0] !== "page")
    .join("&");

  return `${href.slice(0, hashIndex)}#${hashString.slice(0, queryIndex)}${
    keptQueryString ? `?${keptQueryString}` : ""
  }`;
}

function flushGoBackTasks() {
  const pendingPageKeys = new Set(pageKeysToGoBack);
  pageKeysToGoBack.clear();
  if (pendingPageKeys.size === 0) return;

  const openedPageKeys = getOpenedPageKeys();

  // 从栈顶（最后一个 page 参数）往回连续匹配，遇到不属于本次待关闭集合的就停止，
  // 避免跨越仍然打开着的弹窗去 pop 历史记录
  let goBackDelta = 0;
  for (let i = openedPageKeys.length - 1; i >= 0; i--) {
    if (!pendingPageKeys.has(openedPageKeys[i])) break;
    goBackDelta += 1;
  }

  if (goBackDelta > 0) {
    window.history.go(-1 * goBackDelta);
    // 用回退后 URL 上应剩下的参数重写列表
    setPushedPageKeys(
      openedPageKeys.slice(0, openedPageKeys.length - goBackDelta)
    );
  }
}

// 本次文档是否是「刷新」加载的
function isPageReload() {
  const navEntry = performance.getEntriesByType?.("navigation")?.[0];
  if (navEntry) return navEntry.type === "reload";
  // 老浏览器兜底：Navigation Timing Level 1（已废弃但仍可用）
  return performance.navigation?.type === 1;
}

/**
 * 刷新兜底：刷新只会重置内存中的弹窗状态，刷新前 pushState 压入的历史记录仍留在会话历史栈里。
 * 后果有两个：URL 上的 page 参数与真实弹窗状态脱钩（重新打开同名弹窗时会跳过 pushState），
 * 以及用户要多按 N 次物理返回键才能真正离开当前页。
 * 应用启动完成后（router.isReady）调用一次。
 */
export function cleanupStalePagePopupHistory() {
  const pushedPageKeys = getPushedPageKeys();
  const urlPageKeys = getOpenedPageKeys();
  Persistent.removeSession(SESSION_KEY_PAGE_POPUP_PUSHED_KEYS);

  if (pushedPageKeys.length === 0 && urlPageKeys.length > 0) {
    const cleanedHref = removePageParamsFromHref(location.href);
    if (cleanedHref === location.href) return;
    window.history.replaceState(window.history.state, "", cleanedHref);
  } else {
    // 从栈顶（最后一个 page 参数）往下连续匹配，只回收「在 URL 上」且
    // 「确实是本 tab pushState 压入的」那部分。回退条数始终以 URL 为准，
    // 这样即使列表因边界情况比实际记录长，也不会退过头
    let staleCount = 0;
    for (let i = urlPageKeys.length - 1; i >= 0; i--) {
      if (!pushedPageKeys.includes(urlPageKeys[i])) break;
      staleCount += 1;
    }
    if (staleCount === 0) return;

    // 非刷新时不回退：那些记录并不在历史栈里
    if (!isPageReload()) return;
    // history.length 含当前条目。不够退时 go(-n) 会被浏览器 clamp 到栈底第一条并真的跳过去，
    // 可能把用户弹出应用，故跳过
    if (window.history.length <= staleCount) return;

    window.history.go(-1 * staleCount);
  }
}
